import { useCallback, useMemo, useState, type ReactNode } from "react"
import { Snackbar, type SnackbarItem } from "../components/Snackbar/Snackbar"
import { SnackbarContext, type ShowSnackbarOptions } from "./snackbarContext"

const DEFAULT_DURATION_MS = 4500

let snackbarSequence = 0

interface SnackbarProviderProps {
  children: ReactNode
}

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [items, setItems] = useState<SnackbarItem[]>([])

  const showSnackbar = useCallback((options: ShowSnackbarOptions) => {
    snackbarSequence += 1

    const id = `snackbar-${Date.now()}-${snackbarSequence}`
    const item: SnackbarItem = {
      id,
      title: options.title,
      description: options.description,
      variant: options.variant ?? "info",
      durationMs: Math.max(0, options.durationMs ?? DEFAULT_DURATION_MS),
    }

    setItems((currentItems) => [...currentItems, item])

    return id
  }, [])

  const dismissSnackbar = useCallback((id?: string) => {
    setItems((currentItems) => {
      if (!id) {
        return currentItems.slice(1)
      }

      return currentItems.filter((item) => item.id !== id)
    })
  }, [])

  const value = useMemo(
    () => ({ showSnackbar, dismissSnackbar }),
    [dismissSnackbar, showSnackbar],
  )

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar item={items[0]} onClose={dismissSnackbar} />
    </SnackbarContext.Provider>
  )
}
